/**
 * Timezone utilities for São Paulo, Brazil timezone
 */

/**
 * Get current time in São Paulo timezone
 */
export function getSaoPauloTime(): Date {
  const now = new Date();
  const saoPauloTime = new Date(
    now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })
  );
  return saoPauloTime;
}

/**
 * Get current hour in São Paulo timezone (0-23)
 */
export function getSaoPauloHour(): number {
  const saoPauloTime = getSaoPauloTime();
  return saoPauloTime.getHours();
}

/**
 * Check if current time is after business hours (after configured hour São Paulo time)
 * Only returns true within 1 hour after the configured logout hour to prevent logout all day
 */
export function isAfterBusinessHours(logoutHour: number = 20): boolean {
  const now = getSaoPauloTime();
  const hour = now.getHours();
  const minutes = now.getMinutes();

  // Current time in minutes since midnight
  const currentMinutes = hour * 60 + minutes;
  const logoutMinutes = logoutHour * 60;

  // Auto-logout only within 1 hour (60 minutes) after the configured hour
  // This prevents logout happening all day long
  const isInLogoutWindow = currentMinutes >= logoutMinutes && currentMinutes < (logoutMinutes + 60);

  return isInLogoutWindow;
}

/**
 * Check if we should logout based on business hours
 * Returns true if:
 * - Current time is within 1 hour after configured logout hour São Paulo time AND
 * - Last login was before configured logout hour of the same day
 */
export function shouldAutoLogout(lastLoginTimestamp: number, logoutHour: number = 20): boolean {
  if (!isAfterBusinessHours(logoutHour)) {
    return false;
  }

  const now = getSaoPauloTime();
  const lastLogin = new Date(lastLoginTimestamp);

  // Convert last login to São Paulo time
  const lastLoginSaoPaulo = new Date(
    lastLogin.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })
  );

  // Get the date part only (ignoring time)
  const todayDate = now.toLocaleDateString('en-US', { timeZone: 'America/Sao_Paulo' });
  const lastLoginDate = lastLoginSaoPaulo.toLocaleDateString('en-US', { timeZone: 'America/Sao_Paulo' });

  // If last login was on a different day, don't logout
  if (todayDate !== lastLoginDate) {
    return false;
  }

  // Same day - check if last login was before configured logout hour
  const lastLoginHour = lastLoginSaoPaulo.getHours();
  return lastLoginHour < logoutHour;
}

/**
 * Format São Paulo time as string
 */
export function formatSaoPauloTime(date?: Date): string {
  const timeToFormat = date || getSaoPauloTime();
  return timeToFormat.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}
